import type { VendorTheme } from '../types/storefront'

/** "#0c2c82" -> "12 44 130", the channel triplet the token layer expects. */
function hexToChannels(hex: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`
}

function parseChannels(channels: string): [number, number, number] {
  const [r, g, b] = channels.split(/\s+/).map(Number)
  return [r, g, b]
}

function formatChannels(r: number, g: number, b: number): string {
  return `${Math.round(r)} ${Math.round(g)} ${Math.round(b)}`
}

/** Blend each channel toward white (amount 0..1). Used for primary-fixed tints. */
function mixTowardWhite(channels: string, amount: number): string {
  const [r, g, b] = parseChannels(channels)
  const mix = (c: number) => c + (255 - c) * amount
  return formatChannels(mix(r), mix(g), mix(b))
}

/** Blend each channel toward black (amount 0..1). Used for on-primary-fixed text. */
function mixTowardBlack(channels: string, amount: number): string {
  const [r, g, b] = parseChannels(channels)
  const mix = (c: number) => c * (1 - amount)
  return formatChannels(mix(r), mix(g), mix(b))
}

const PRIMARY_VARS = [
  '--c-primary',
  '--c-surface-tint',
  '--c-primary-container',
  '--c-primary-fixed',
  '--c-primary-fixed-dim',
  '--c-on-primary-fixed',
  '--c-on-primary-fixed-variant',
  '--c-inverse-primary',
  '--c-on-primary-container',
] as const

/**
 * A vendor may override one colour (name, logo, and one primary). Every
 * component reads the token layer, so theming is writing CSS variables.
 *
 * Primary-fixed (tonal buttons, badges) must follow the same primary — otherwise
 * a black theme still shows the Artisanal Azure lavender on "Continue as a guest".
 */
export function applyTheme(theme: VendorTheme | undefined | null) {
  const root = document.documentElement
  if (!theme?.primary) {
    for (const name of PRIMARY_VARS) root.style.removeProperty(name)
    return
  }

  const channels = hexToChannels(theme.primary)
  if (!channels) return

  const fixed = mixTowardWhite(channels, 0.88)
  const fixedDim = mixTowardWhite(channels, 0.72)
  const onFixed = mixTowardBlack(channels, 0.15)
  const onFixedVariant = mixTowardBlack(channels, 0.35)
  const inverse = mixTowardWhite(channels, 0.72)

  root.style.setProperty('--c-primary', channels)
  root.style.setProperty('--c-surface-tint', channels)
  root.style.setProperty('--c-primary-container', channels)
  root.style.setProperty('--c-primary-fixed', fixed)
  root.style.setProperty('--c-primary-fixed-dim', fixedDim)
  root.style.setProperty('--c-on-primary-fixed', onFixed)
  root.style.setProperty('--c-on-primary-fixed-variant', onFixedVariant)
  root.style.setProperty('--c-inverse-primary', inverse)
  root.style.setProperty('--c-on-primary-container', inverse)
}

export function applyFavicon(href: string | null | undefined) {
  if (!href) return
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = href
}
